import { Link } from "react-router";
import type { WalletsResponse } from "@townsy/contracts";
import { useWalletsQuery } from "../api/queries";
import { walletChips } from "../lib/wallet";
import { selectIsGuest, useTownStore } from "../state/store";

/** Amber coin glyph (Coins). */
export function CoinIcon() {
  return (
    <svg className="wallet-icon" viewBox="0 0 16 16" width={14} height={14} aria-hidden="true">
      <circle cx={8} cy={8} r={7} fill="#D99A2B" stroke="#9A6B00" strokeWidth={1} />
      <circle cx={8} cy={8} r={4.2} fill="none" stroke="#FBEFD6" strokeWidth={1.2} />
    </svg>
  );
}

/** Cobalt credit-note glyph (Townsy Credit) — deliberately not a second gold coin. */
export function CreditIcon() {
  return (
    <svg className="wallet-icon" viewBox="0 0 16 16" width={14} height={14} aria-hidden="true">
      <rect x={1} y={3.5} width={14} height={9} rx={2} fill="#2851D9" />
      <rect x={1} y={5.5} width={14} height={1.6} fill="#E7ECFB" />
    </svg>
  );
}

/** Presentational part (pure props; unit-tested with server rendering). */
/**
 * "Earn": the coin chip's second half (M6, ADR-0011 #7) opens the list of sponsored tasks across the
 * town; the balance half keeps opening the wallet.
 */
function EarnButton({ onEarn }: { onEarn(): void }) {
  return (
    <button
      type="button"
      className="wallet-chip wallet-chip-coin wallet-chip-earn"
      onClick={onEarn}
      aria-label="Earn coins: sponsored tasks in town"
      title="Earn coins: sponsored tasks in town"
      data-testid="wallet-chip-earn"
    >
      Earn
    </button>
  );
}

export function WalletChipsView({
  isGuest,
  wallets,
  failed,
  onOpen,
  onEarn,
}: {
  isGuest: boolean;
  wallets: Pick<WalletsResponse, "coins" | "credit"> | null;
  failed: boolean;
  onOpen(): void;
  /** Opens "Earn coins" (sponsored tasks); omitted where that list is not available. */
  onEarn?: () => void;
}) {
  if (isGuest) {
    return (
      <span className="wallet-chips">
        <Link className="btn btn-sm btn-ghost wallet-signup" to="/account" title="Residents collect coins from sponsored tasks">
          <CoinIcon /> Sign up to collect coins
        </Link>
        {onEarn ? <EarnButton onEarn={onEarn} /> : null}
      </span>
    );
  }
  if (!wallets) {
    return (
      <span className="wallet-chips" aria-live="polite">
        <span className="wallet-chip wallet-chip-coin wallet-chip-static" title={failed ? "Balances unavailable" : "Loading balances"}>
          <CoinIcon /> {failed ? "Coins —" : "…"}
        </span>
      </span>
    );
  }
  const [coins, credit] = walletChips(wallets);
  return (
    <span className="wallet-chips" role="group" aria-label="Your balances">
      {[coins, credit].map((chip) => (
        <span key={chip.unit} className={chip.unit === "COIN" && onEarn ? "wallet-chip-pair" : "wallet-chip-single"}>
          <button
            type="button"
            className={chip.className}
            aria-label={`${chip.ariaLabel}. Open wallet`}
            title={chip.unit === "COIN" ? "Coins: earned from sponsored tasks, spent on homes and items" : "Townsy Credit: dollar-denominated, not cash"}
            onClick={onOpen}
            data-testid={chip.unit === "COIN" ? "wallet-chip-coins" : "wallet-chip-credit"}
          >
            {chip.unit === "COIN" ? <CoinIcon /> : <CreditIcon />}
            <span>{chip.text}</span>
          </button>
          {chip.unit === "COIN" && onEarn ? <EarnButton onEarn={onEarn} /> : null}
        </span>
      ))}
    </span>
  );
}

/**
 * HUD balances (docs/01 section 6): two separately labelled chips — "N coins" (amber) and
 * "$X.XX credit" (cobalt) — from `GET /me/wallets`. Never summed, never converted. Guests see a
 * sign-up link instead (they have no wallet).
 */
export function WalletChips() {
  const isGuest = useTownStore(selectIsGuest);
  const hasBootstrap = useTownStore((s) => Boolean(s.bootstrap));
  const setUi = useTownStore((s) => s.setUi);
  const q = useWalletsQuery(hasBootstrap && !isGuest);
  if (!hasBootstrap) return null;
  return (
    <WalletChipsView
      isGuest={isGuest}
      wallets={q.data ?? null}
      failed={Boolean(q.error)}
      onOpen={() => setUi({ walletOpen: true, menuOpen: false })}
      onEarn={() => setUi({ earnOpen: true, menuOpen: false })}
    />
  );
}
